'use client';

import { useEffect, useRef } from 'react';
import { useLocale } from 'next-intl';
import { usePathname } from '@/i18n/navigation';
import {
  CONSENT_CHANGED_EVENT,
  getStoredConsent,
  isConsentWithdrawn,
  type CookieConsent,
} from '@/lib/cookie-consent';
import { applyConsent, clearTrackingCookies, trackPageView, updateGoogleConsent } from '@/lib/analytics/client';

/**
 * Loads GA4 and the Meta Pixel according to the visitor's cookie choice
 * (see src/lib/analytics/client.ts for why GA4 is loaded directly rather than
 * through the GTM container). Mounted in the [locale] layout only, so admin
 * and login pages are never tracked.
 *
 * Granting a category loads its tag on the spot. Withdrawing one deletes the
 * tracking cookies and reloads the page — a loaded script cannot be unloaded.
 */
export function TrackingScripts() {
  useEffect(() => {
    let current: CookieConsent | null = getStoredConsent();
    applyConsent(current);

    const handleConsentChange = (e: Event) => {
      const next = (e as CustomEvent<CookieConsent>).detail;
      if (isConsentWithdrawn(current, next)) {
        // Deny storage first so gtag stops re-writing its cookies (it does
        // so on unload), then delete them and reload to unload the scripts.
        if (next) updateGoogleConsent(next);
        clearTrackingCookies({
          analytics: !!current?.analytics && !next?.analytics,
          marketing: !!current?.marketing && !next?.marketing,
        });
        window.location.reload();
        return;
      }
      current = next;
      applyConsent(next);
    };
    window.addEventListener(CONSENT_CHANGED_EVENT, handleConsentChange);
    return () => window.removeEventListener(CONSENT_CHANGED_EVENT, handleConsentChange);
  }, []);

  // In-app navigations → page_view (GA4's own history detection doesn't see
  // Next.js router navigations; see trackPageView). The first render is the
  // landing page, which GA4's `config` already reports.
  const locale = useLocale();
  const pathname = usePathname();
  const routeKey = `${locale}:${pathname}`;
  const lastRoute = useRef<string | null>(null);
  useEffect(() => {
    if (lastRoute.current === null) {
      lastRoute.current = routeKey;
      return;
    }
    if (lastRoute.current === routeKey) return;
    lastRoute.current = routeKey;
    trackPageView();
  }, [routeKey]);

  return null;
}
